import { useEffect, useState } from 'react';
import { normalizeMediaFetchUrl } from '@/helpers/preview/blob-asset-key.helper';
import { ensurePreviewBlobCached, getCachedPreviewBlobObjectUrl } from '@/helpers/preview/preview-blob-cache.helper';

/**
 * Resolve a signed blob URL to a cached object URL for pdf.js / media preview.
 * Keeps the previous frame visible while the next slide loads (no black flash).
 */
export function useCachedBlobUrl(
  rawUrl: string | undefined,
  assetKeyOverride?: string,
): { url: string; loading: boolean; error: boolean } {
  const normalizedUrl = rawUrl ? normalizeMediaFetchUrl(rawUrl) : undefined;

  const [displayUrl, setDisplayUrl] = useState(() => {
    if (assetKeyOverride && normalizedUrl) {
      return getCachedPreviewBlobObjectUrl(assetKeyOverride, normalizedUrl) ?? '';
    }
    return '';
  });
  const [error, setError] = useState(false);
  const [pendingLoad, setPendingLoad] = useState(false);

  useEffect(() => {
    if (!normalizedUrl) {
      setDisplayUrl('');
      setError(false);
      setPendingLoad(false);
      return;
    }

    let cancelled = false;

    const syncCached = assetKeyOverride ? getCachedPreviewBlobObjectUrl(assetKeyOverride, normalizedUrl) : undefined;
    if (syncCached) {
      setDisplayUrl(syncCached);
      setError(false);
      setPendingLoad(false);
      return;
    }

    setError(false);
    setPendingLoad(true);

    void ensurePreviewBlobCached(normalizedUrl, assetKeyOverride)
      .then((cached) => {
        if (!cancelled) {
          setDisplayUrl(cached);
          setPendingLoad(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setPendingLoad(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [normalizedUrl, assetKeyOverride]);

  if (!normalizedUrl) {
    return { url: '', loading: false, error: false };
  }

  return {
    url: displayUrl,
    loading: pendingLoad && !displayUrl && !error,
    error,
  };
}
